import { User } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import {
  IBlockEditBaseline,
  IEditTicket,
  IEditableBlock,
  returnBlockEditBaseline,
  returnBlockEditEndpoint,
  returnBlockEditPatch,
  returnEditInstrument,
} from "@/components/widgets/tradeFromScratch/helper/blockEditHelper";
import { logClientError } from "@/lib/errorLoggingACTIONS";
import { fetchRequestWrapper } from "@/lib/utilities";
import draftLegsStore, { IDraftLeg } from "./draftLegsStore";
import { refreshUnassignedBlocks } from "./importBlocksAndAllocationsStoreACTIONS";
import tradeFromScratchStore, {
  TicketInstrument,
  ensureTicketForInstrument,
  returnTicketForInstrument,
  updateTicketForInstrument,
} from "./tradeFromScratchStore";
import { sendTicketForInstrument } from "./tradeFromScratchStoreACTIONS";

// LQC-2941 - the draft leg lifecycle: add, save through the same send path Leg Creation
// uses, or discard. Every exit drops the draft's ticket so none are left behind.

export const DRAFT_LEG_ID_PREFIX = "draft-";

export function isDraftLegId(id: string | undefined | null): boolean {
  return !!id && id.startsWith(DRAFT_LEG_ID_PREFIX);
}

// Instruments whose required fields (maturity, expiry, option type, termination) live in
// Security Details, so the panel starts open rather than hiding them.
export const DRAFT_LEG_INSTRUMENTS_WITH_DETAILS_OPEN: TicketInstrument[] = [
  TicketInstrument.Swap,
  TicketInstrument.Swaption,
  TicketInstrument.BondOption,
  TicketInstrument.Repo,
];

export function addDraftLeg(widgetId: string, instrument: TicketInstrument): string {
  const id = `${DRAFT_LEG_ID_PREFIX}${crypto.randomUUID()}`;
  ensureTicketForInstrument(id, instrument);
  draftLegsStore.getState().addDraftLeg(widgetId, { id, instrument });
  if (DRAFT_LEG_INSTRUMENTS_WITH_DETAILS_OPEN.includes(instrument)) {
    draftLegsStore.getState().setSecurityDetailsOpen(id, true);
  }
  return id;
}

export function discardDraftLeg(widgetId: string, draftId: string) {
  draftLegsStore.getState().removeDraftLeg(widgetId, draftId);
  tradeFromScratchStore.getState().removeTickets(draftId);
}

// LQC-3027 - reopens a saved block as an editable leg, filled from the block. The id
// is stable per block, so a second click on the same block's pencil does nothing.
export function startBlockEdit(widgetId: string, block: IEditableBlock): string | null {
  const instrument = returnEditInstrument(block);
  if (!instrument) return null;
  const id = `${DRAFT_LEG_ID_PREFIX}edit-${block.id}`;
  const existing = draftLegsStore.getState().draftLegsByWidget[widgetId] ?? [];
  if (existing.some((draftLeg) => draftLeg.id === id)) return id;
  ensureTicketForInstrument(id, instrument);
  const baseline = returnBlockEditBaseline(instrument, block);
  // The baseline keeps the ticket's own trade date and empty External ID, so neither reads as a change.
  updateTicketForInstrument(id, instrument, baseline.ticket);
  const seeded = returnTicketForInstrument(
    tradeFromScratchStore.getState(),
    instrument,
    id,
  ) as IEditTicket;
  draftLegsStore
    .getState()
    .addDraftLeg(widgetId, { id, instrument, edit: { ...baseline, ticket: { ...seeded } } });
  return id;
}

// PATCH only what changed (edit spec F1). Nothing changed: the draft just closes.
async function saveBlockEdit({
  widgetId,
  draftLeg,
  getAccessTokenSilently,
}: {
  widgetId: string;
  draftLeg: IDraftLeg & { edit: IBlockEditBaseline };
  getAccessTokenSilently: Function;
}): Promise<boolean> {
  const { id, instrument, edit } = draftLeg;
  const store = tradeFromScratchStore.getState();
  if (store.isSendingByTicketId[id]) return false;
  const ticket = returnTicketForInstrument(store, instrument, id) as IEditTicket;
  const patch = returnBlockEditPatch(edit, ticket);
  if (!patch) {
    toast.info(`No changes to leg ${edit.blockId}.`);
    discardDraftLeg(widgetId, id);
    return true;
  }
  store.setIsSendingTicket(id, true);
  try {
    await fetchRequestWrapper(
      {
        url: returnBlockEditEndpoint(instrument, edit.blockId),
        method: "PATCH",
        objToSend: patch,
      },
      getAccessTokenSilently,
    );
    toast.success(`Leg ${edit.blockId} updated.`);
    discardDraftLeg(widgetId, id);
    await refreshUnassignedBlocks(getAccessTokenSilently);
    return true;
  } catch (error) {
    // fetchRequestWrapper has toasted; the draft stays so the user can correct and retry.
    logClientError("draftLegsStoreACTIONS.saveBlockEdit", error, getAccessTokenSilently);
    return false;
  } finally {
    tradeFromScratchStore.getState().setIsSendingTicket(id, false);
  }
}

// On success the real block arrives through refreshUnassignedBlocks, so the draft goes.
export async function saveDraftLeg({
  widgetId,
  draftId,
  instrument,
  user,
  getAccessTokenSilently,
}: {
  widgetId: string;
  draftId: string;
  instrument: TicketInstrument;
  user: User | undefined;
  getAccessTokenSilently: Function;
}): Promise<boolean> {
  const draftLeg = (draftLegsStore.getState().draftLegsByWidget[widgetId] ?? []).find(
    (leg) => leg.id === draftId,
  );
  if (draftLeg?.edit) {
    return saveBlockEdit({
      widgetId,
      draftLeg: draftLeg as IDraftLeg & { edit: IBlockEditBaseline },
      getAccessTokenSilently,
    });
  }
  const sent = await sendTicketForInstrument(instrument, {
    ticketId: draftId,
    user,
    getAccessTokenSilently,
  });
  if (sent) discardDraftLeg(widgetId, draftId);
  return sent;
}
